/**
 * **Firestore 쓰기 게이트웨이 — 생짜 CUD 는 여기«만».**
 * `actions/firestore/sdk/**`(무엇을 쓸지) → 이 파일(읽기전용 가드 + 프리미티브 6 래핑)
 * → `firebase/firestore`(setDoc·addDoc·updateDoc·deleteDoc·batch·txn).
 *
 * 읽기 짝은 `firestore-kit.ts`(청크·행 빚기). 이 파일은 그 «쓰기» 대칭이 아니라 **강제되는
 * 입구**다 — 모든 생짜 쓰기가 여기를 지나고, 진입에서 읽기전용 정책을 판정한다.
 *
 * ## ④-e 가 지킨다
 * `setDoc·addDoc·updateDoc·deleteDoc·writeBatch·runTransaction` 를 `'firebase/firestore'` 에서
 * import 하는 자리는 **이 파일 하나**다. 그 밖이면 게이트가 빨강(`tools/firestore-write-check.py`).
 *
 * ## 읽기전용 판정 (`src/constants/firestore-write-policy.ts`)
 * `FIRESTORE_READONLY` 가 false 면 그대로 실행(정상 · 프로덕션·사람 dev). true 이면
 * `opts.allowInReadonly === true`(상황적 1회 허용) 또는 `opts.tag ∈ READONLY_ALLOWED_TAGS`
 * (기본 예외 · 부수쓰기 R)일 때만 실행하고, 그 밖은 `FirestoreReadonlyError` 로 reject 한다.
 *
 * 읽기는 손대지 않는다 — 부수쓰기 R(checkedList 의 읽기)은 값을 돌려주고, 그 안의
 * setDoc 만 이 게이트를 지난다(태그로 통과).
 */

import {
  addDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  arrayUnion,
  writeBatch,
  runTransaction,
  type Firestore,
  type FieldPath,
  serverTimestamp,
  type UpdateData,
  type Transaction,
  type DocumentData,
  type WithFieldValue,
  type DocumentReference,
  type CollectionReference,
  type PartialWithFieldValue,
} from 'firebase/firestore';

import { chunkArray } from 'src/lib/firebase/firestore-kit';
import { FIRESTORE_READONLY, READONLY_ALLOWED_TAGS } from 'src/constants/firestore-write-policy';

/** `arrayUnion` 한 번에 넣는 원소 수 상한 */
const ARRAY_UNION_LIMIT = 200;

// ----------------------------------------------------------------------

/** 호출부가 읽기전용 예외를 다는 자리 */
export type WriteOpts = {
  /** 이 태그가 `READONLY_ALLOWED_TAGS` 에 있으면 읽기전용에도 통과(기본 예외) */
  tag?: string;
  /** 이 한 호출만 상황적으로 허용(태그 없이) */
  allowInReadonly?: boolean;
};

/** 읽기전용 게이트가 막은 쓰기. `op`·`path` 로 어디서 막혔는지 드러낸다 */
export class FirestoreReadonlyError extends Error {
  constructor(
    public readonly op: string,
    public readonly path: string,
    public readonly tag?: string,
  ) {
    super(
      `[firestore-write] 읽기전용: «${op}» 차단 @ ${path}` +
        (tag ? ` (태그 ${tag} 은 허용 목록에 없다)` : ' (태그·override 없음)'),
    );
    this.name = 'FirestoreReadonlyError';
  }
}

/** 모든 쓰기 프리미티브가 진입에서 부른다. 통과면 return, 막으면 throw — 함수가 async 라 이 throw 는 reject 로 나간다(fire-and-forget .catch 도 잡게) */
function guard(op: string, path: string, opts?: WriteOpts): void {
  if (!FIRESTORE_READONLY) return;
  if (opts?.allowInReadonly) return;
  if (opts?.tag && READONLY_ALLOWED_TAGS.has(opts.tag)) return;
  throw new FirestoreReadonlyError(op, path, opts?.tag);
}

// ---- C (create) --------------------------------------------------------

/** `setDoc` — 통짜 쓰기(문서 신규/덮어쓰기) */
export async function writeDoc<T = DocumentData>(
  ref: DocumentReference<T>,
  data: WithFieldValue<T>,
  opts?: WriteOpts,
): Promise<void> {
  guard('setDoc', ref.path, opts);
  return setDoc(ref, data);
}

/** `setDoc(merge:true)` — 부분 병합(문서 없어도 됨) */
export async function mergeDoc<T = DocumentData>(
  ref: DocumentReference<T>,
  data: PartialWithFieldValue<T>,
  opts?: WriteOpts,
): Promise<void> {
  guard('setDoc(merge)', ref.path, opts);
  return setDoc(ref, data, { merge: true });
}

/** `addDoc` — 자동 id 로 컬렉션에 추가 */
export async function addToCollection<T = DocumentData>(
  coll: CollectionReference<T>,
  data: WithFieldValue<T>,
  opts?: WriteOpts,
): Promise<DocumentReference<T>> {
  guard('addDoc', coll.path, opts);
  return addDoc(coll, data);
}

// ---- U (update) --------------------------------------------------------

/** `updateDoc` — 부분 갱신(문서 없으면 throw). `arrayUnion`·`increment`·`deleteField` 는 data 인자로 */
export async function patchDoc<T = DocumentData>(
  ref: DocumentReference<T>,
  data: UpdateData<T>,
  opts?: WriteOpts,
): Promise<void> {
  guard('updateDoc', ref.path, opts);
  // Firestore 의 updateDoc 오버로드(필드-값 쌍 변이)와 제네릭이 부딪혀 DocumentData 로 좁혀 부른다.
  return updateDoc(ref as DocumentReference<DocumentData>, data as UpdateData<DocumentData>);
}

/**
 * `updateDoc` 의 «필드-값 쌍» 가변인자 오버로드 — `FieldPath`(점이 든 필드명 등)를 담아야 할 때.
 * `patchDoc`(객체)로는 표현 못 한다: 객체 키의 점을 Firestore 가 중첩으로 쪼갠다
 * (`'instagram.com'` → `{instagram:{com}}`). 그래서 필드를 `FieldPath` 로
 * 넘기는 이 꼴이 유일한 길이다. `pairs` 를 `[필드, 값]` 배열로 받아 SDK 가변인자로 편다.
 */
export async function updateFields(
  ref: DocumentReference<unknown>,
  pairs: ReadonlyArray<readonly [string | FieldPath, unknown]>,
  opts?: WriteOpts,
): Promise<void> {
  guard('updateDoc(fields)', ref.path, opts);
  if (pairs.length === 0) return;
  const [first, ...rest] = pairs;
  await updateDoc(
    ref as DocumentReference<DocumentData>,
    first[0],
    first[1],
    ...rest.flatMap((p) => [p[0], p[1]]),
  );
}

// ---- D (delete) --------------------------------------------------------

/** `deleteDoc` — 문서 삭제 */
export async function removeDoc(ref: DocumentReference<unknown>, opts?: WriteOpts): Promise<void> {
  guard('deleteDoc', ref.path, opts);
  return deleteDoc(ref);
}

/**
 * 배열 필드에 `arrayUnion` 으로 이어붙인다 — 상한 200 개씩 청크로 나눠 여러 번 `mergeDoc`.
 * `extra` 를 주면 매 청크에 함께 넣는다(원본과 같은 청크로 들어가야 하는 값). `updatedAt` 은 항상 찍는다.
 * 내부는 가드된 `mergeDoc` 이라 청크마다 읽기전용 판정을 지난다.
 */
export async function appendToArrayField(
  ref: DocumentReference,
  field: string,
  values: unknown[],
  extra?: (chunk: unknown[]) => Promise<DocumentData> | DocumentData,
  opts?: WriteOpts,
): Promise<void> {
  if (!Array.isArray(values) || values.length === 0) return;

  for (const chunk of chunkArray(values, ARRAY_UNION_LIMIT)) {
    const extraPayload = extra ? await extra(chunk) : {};
    await mergeDoc(
      ref,
      { [field]: arrayUnion(...chunk), ...extraPayload, updatedAt: serverTimestamp() },
      opts,
    );
  }
}

// ---- 혼합 (batch · transaction) ----------------------------------------

/**
 * `writeBatch` — 콜백 안에서 batch.set/update/delete 를 모아 원자적으로 커밋.
 * 진입에서 가드하므로, 읽기전용이면 배치가 시작조차 안 한다.
 */
export async function runWriteBatch(
  db: Firestore,
  fn: (batch: ReturnType<typeof writeBatch>) => void,
  opts?: WriteOpts,
): Promise<void> {
  guard('writeBatch', '(batch)', opts);
  const batch = writeBatch(db);
  fn(batch);
  return batch.commit();
}

/**
 * `runTransaction` — 읽고-쓰는 트랜잭션. 진입에서 가드한다.
 * ⚠️ 트랜잭션은 읽기도 포함하지만, 쓰기 목적이라 통째로 가드한다(읽기 전용 트랜잭션은 드물다).
 */
export async function runTxn<T>(
  db: Firestore,
  fn: (txn: Transaction) => Promise<T>,
  opts?: WriteOpts,
): Promise<T> {
  guard('runTransaction', '(transaction)', opts);
  return runTransaction(db, fn);
}
